import React, { useState } from 'react';
import { useCoin } from '../CoinContext';
import { supabase } from '../supabaseClient';
import dustIcon from '../assets/dust.png';
import { useAffiliateHistory } from '../hooks/useAffiliateHistory';
import { useDustRanking } from '../hooks/useDustRanking';
import { useClaimItems } from '../hooks/useClaimItems';

export default function MarketModal({ open, onClose }) {
  const [activeTab, setActiveTab] = useState('point');
  const { walletAddress, currentPoints = 0, dustPoints = 0, refreshDustPoints } = useCoin();
  const [itemLink, setItemLink] = useState('');
  const [affiliateUri, setAffiliateUri] = useState(''); // 생성된 제휴 링크
  const { histories, loading, refresh, canRefresh, lastRefreshTime } = useAffiliateHistory(walletAddress);

  // 랭킹 사이클 생성 (UTC+0 기준 월별)
  const generateCycles = () => {
    const now = new Date();
    const currentYear = now.getUTCFullYear();
    const currentMonth = now.getUTCMonth();

    const cycles = [];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    // 현재 월과 이전 월들 포함 (최대 3개월)
    for (let i = 0; i <= 2; i++) {
      let targetMonth = currentMonth - i;
      let targetYear = currentYear;

      if (targetMonth < 0) {
        targetMonth += 12;
        targetYear -= 1;
      }

      const start = new Date(Date.UTC(targetYear, targetMonth, 1, 0, 0, 0, 0));
      const end = new Date(Date.UTC(targetYear, targetMonth + 1, 0, 23, 59, 59, 999));

      cycles.push({
        label: `${monthNames[targetMonth]} ${targetYear} (${start.toISOString().slice(0, 10)} ~ ${end.toISOString().slice(0, 10)})`,
        start: start.toISOString(),
        end: end.toISOString(),
      });
    }

    return cycles;
  };

  // 모달이 열릴 때마다 cycles 재계산 (월이 바뀌어도 자동 반영)
  const cycles = React.useMemo(() => generateCycles(), [open]);
  const [selectedCycle, setSelectedCycle] = useState(() => generateCycles()[0]);

  // 모달이 열릴 때 현재 월로 selectedCycle 업데이트
  React.useEffect(() => {
    if (open) {
      const newCycles = generateCycles();
      setSelectedCycle(newCycles[0]);
    }
  }, [open]);

  const { rankings, myRank, loading: rankingLoading, error: rankingError } = useDustRanking(walletAddress, selectedCycle);
  const { claimItems, loading: claimLoading, hasTradedToday, claimedToday, claimReward, refresh: refreshClaimItems } = useClaimItems(walletAddress);

  // 이전 달 랭킹 보상용 cycle 생성
  const prevMonthCycle = React.useMemo(() => {
    const now = new Date();
    let prevMonth = now.getUTCMonth() - 1;
    let prevYear = now.getUTCFullYear();
    if (prevMonth < 0) {
      prevMonth = 11;
      prevYear -= 1;
    }
    const start = new Date(Date.UTC(prevYear, prevMonth, 1, 0, 0, 0, 0));
    const end = new Date(Date.UTC(prevYear, prevMonth + 1, 0, 23, 59, 59, 999));
    return { start: start.toISOString(), end: end.toISOString() };
  }, []);

  // 이전 달 랭킹 조회
  const { myRank: prevMonthMyRank } = useDustRanking(walletAddress, prevMonthCycle);
  const [countdown, setCountdown] = useState(null);
  const [claimingItemId, setClaimingItemId] = useState(null);
  const [agreedToRules, setAgreedToRules] = useState(false);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [alertModal, setAlertModal] = useState({ show: false, title: '', message: '', type: 'info' });
  const [rankingRewardClaimed, setRankingRewardClaimed] = useState(false);
  const [claimingRankingReward, setClaimingRankingReward] = useState(false);

  // 이전 달 랭킹 보상 클레임 여부 확인
  React.useEffect(() => {
    const checkRankingRewardClaimed = async () => {
      if (!walletAddress) return;

      const now = new Date();
      let prevMonth = now.getUTCMonth(); // 0-11
      let prevYear = now.getUTCFullYear();
      if (prevMonth === 0) {
        prevMonth = 12;
        prevYear -= 1;
      }

      const { data, error } = await supabase
        .from('ranking_reward_claims')
        .select('id')
        .eq('wallet_address', walletAddress)
        .eq('reward_year', prevYear)
        .eq('reward_month', prevMonth)
        .maybeSingle();

      if (!error && data) {
        setRankingRewardClaimed(true);
      } else {
        setRankingRewardClaimed(false);
      }
    };

    checkRankingRewardClaimed();
  }, [walletAddress, open]);

  // 랭킹 보상 클레임 함수
  const handleClaimRankingReward = async () => {
    if (claimingRankingReward) return;

    try {
      setClaimingRankingReward(true);

      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        showAlert('Please login again', 'Session Expired', 'warning');
        return;
      }

      const { data, error } = await supabase.functions.invoke('claim-ranking-reward', {});

      if (error) {
        const errorMessage = data?.error || error.message || 'Failed to claim ranking reward';
        throw new Error(errorMessage);
      }

      if (data.success) {
        setRankingRewardClaimed(true);
        showAlert(
          `You received ${data.quantity}x ${data.rewardMonth} Zodiac Pet!`,
          'Ranking Reward Claimed!',
          'success'
        );
      } else {
        throw new Error(data.error || 'Failed to claim');
      }
    } catch (err) {
      console.error('Ranking reward claim error:', err);
      showAlert(err.message || 'Failed to claim ranking reward', 'Claim Failed', 'error');
    } finally {
      setClaimingRankingReward(false);
    }
  };

  const showAlert = (message, title = 'Notice', type = 'info') => {
    setAlertModal({ show: true, title, message, type });
  };

  // 모달이 열릴 때 dustPoints 새로고침
  React.useEffect(() => {
    if (open && walletAddress) {
      refreshDustPoints();
    }
  }, [open, walletAddress, refreshDustPoints]);

  // 카운트다운 타이머
  React.useEffect(() => {
    if (!canRefresh && lastRefreshTime) {
      const interval = setInterval(() => {
        const remaining = Math.ceil((60000 - (Date.now() - lastRefreshTime)) / 1000);
        if (remaining > 0) {
          setCountdown(remaining);
        } else {
          setCountdown(null);
        }
      }, 100);

      return () => clearInterval(interval);
    } else {
      setCountdown(null);
    }
  }, [canRefresh, lastRefreshTime]);

  // 탭 변경 시 dustPoints 새로고침
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (walletAddress) {
      refreshDustPoints();
    }
  };

  if (!open) return null;

  const handleCreateLink = async () => {
    if (!walletAddress) {
      showAlert('Please connect your wallet first!', 'Wallet Required', 'warning');
      return;
    }
    if (!itemLink.trim()) {
      showAlert('Please enter an item link!', 'Input Required', 'warning');
      return;
    }
    if (!agreedToRules) {
      showAlert('Please agree to the Affiliate link rules first!', 'Agreement Required', 'warning');
      return;
    }

    // 링크 형식 검증
    const validLinkPattern = /^https:\/\/msu\.io\/marketplace\/(nft|character)\/\d+$/;
    if (!validLinkPattern.test(itemLink.trim())) {
      showAlert(
        'Invalid link format. Please enter a valid MSU marketplace link.\n\n' +
        'Examples:\n• https://msu.io/marketplace/nft/8338345088485752938083068529410\n• https://msu.io/marketplace/character/555073495721272706951540677446240244832372314321',
        'Invalid Link Format',
        'error'
      );
      return;
    }

    try {
      // Edge Function 호출 (API KEY는 서버에서 관리)
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        showAlert('Please login again', 'Session Expired', 'warning');
        return;
      }

      const { data, error } = await supabase.functions.invoke('create-affiliate-link', {
        body: {
          itemLink: itemLink,
          walletAddress: walletAddress,
        },
      });

      if (error) {
        const errorMessage = data?.error || error.message || 'Failed to create affiliate link';
        throw new Error(errorMessage);
      }

      if (data.success && data.uri) {
        setAffiliateUri(data.uri);
        showAlert('Affiliate link created successfully!', 'Success', 'success');
      } else {
        showAlert(data.error || 'Link created but no URI returned', 'Error', 'error');
      }

      // 입력 필드 초기화
      setItemLink('');

    } catch (error) {
      console.error('Error creating affiliate link:', error);
      showAlert(error.message, 'Error', 'error');
    }
  };

  const handleOpenAffiliateLink = () => {
    if (affiliateUri) {
      window.open(affiliateUri, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999,
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '90%',
          maxWidth: 480,
          maxHeight: '85vh',
          background: 'linear-gradient(180deg, #4FC3D4 0%, #3AA8B8 100%)',
          borderRadius: 24,
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          position: 'relative',
          padding: '32px 20px 20px',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, rgba(255,255,255,0.08) 0%, transparent 50%)
            `,
            pointerEvents: 'none',
          }}
        />
        {/* 새로고침 버튼 - 더스트 위젯 위 */}
        <button
          onClick={async () => {
            await refresh();
            refreshClaimItems();
          }}
          disabled={!canRefresh}
          style={{
            border: 'none',
            background: canRefresh ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.15)',
            fontSize: countdown !== null ? 12 : 16,
            position: 'absolute',
            top: 40,
            left: 24,
            cursor: canRefresh ? 'pointer' : 'not-allowed',
            width: 32,
            height: 32,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontWeight: 'bold',
            zIndex: 10,
            transition: 'all 0.3s',
            opacity: canRefresh ? 1 : 0.5,
          }}
          title={canRefresh ? 'Refresh' : `Wait ${countdown}s`}
        >
          {countdown !== null ? countdown : '🔄'}
        </button>

        {/* 닫기 버튼 - 오른쪽 상단 */}
        <button
          onClick={onClose}
          style={{
            border: 'none',
            background: 'rgba(255,255,255,0.3)',
            fontSize: 20,
            position: 'absolute',
            top: 16,
            right: 16,
            cursor: 'pointer',
            width: 32,
            height: 32,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontWeight: 'bold',
            zIndex: 10,
          }}
        >
          ✕
        </button>
        <h2
          style={{
            margin: '0 0 16px 0',
            textAlign: 'center',
            color: '#fff',
            fontSize: 28,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '1px',
            textShadow: '0 2px 4px rgba(0,0,0,0.2)',
            position: 'relative',
          }}
        >
          • MARKET •
        </h2>

        {/* Points Display & Rewards */}
        <div
          style={{
            marginBottom: 16,
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            gap: 12,
          }}
        >
          {/* Current Dusts */}
          <div
            style={{
              flex: 1,
              background: 'rgba(255,255,255,0.95)',
              borderRadius: 12,
              padding: '12px 16px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
          >
            <div
              style={{
                fontSize: 11,
                color: '#666',
                marginBottom: 4,
                fontWeight: 600,
                textTransform: 'uppercase',
              }}
            >
              Current Dusts
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <img
                src={dustIcon}
                alt="dust"
                style={{
                  width: 24,
                  height: 24,
                }}
              />
              <span
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: '#3AA8B8',
                }}
              >
                {dustPoints.toLocaleString()}
              </span>
            </div>
          </div>

          {/* Monthly Ranking Reward */}
          <div
            style={{
              flex: 1,
              background: 'rgba(255,255,255,0.95)',
              borderRadius: 12,
              padding: '12px 16px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
          >
            <div
              style={{
                fontSize: 11,
                color: '#666',
                marginBottom: 6,
                fontWeight: 600,
                textTransform: 'uppercase',
              }}
            >
              Monthly Ranking Reward
            </div>
            <div style={{ fontSize: 12, lineHeight: 1.6, color: '#333' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                <span style={{ fontWeight: 600 }}>🥇 1st:</span>
                <span>Epic Pet ×60</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                <span style={{ fontWeight: 600 }}>🥈 2nd:</span>
                <span>Epic Pet ×30</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: 600 }}>🥉 3rd:</span>
                <span>Epic Pet ×10</span>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div
          style={{
            display: 'flex',
            gap: 8,
            marginBottom: 20,
            position: 'relative',
            zIndex: 1,
          }}
        >
          {['point', 'claim', 'ranking', 'history'].map((tab) => (
            <button
              key={tab}
              onClick={() => handleTabChange(tab)}
              style={{
                flex: 1,
                padding: '10px 16px',
                border: 'none',
                borderRadius: 12,
                background: activeTab === tab
                  ? 'rgba(255,255,255,0.95)'
                  : 'rgba(255,255,255,0.3)',
                color: activeTab === tab ? '#3AA8B8' : '#fff',
                fontSize: 14,
                fontWeight: 700,
                cursor: 'pointer',
                textTransform: 'uppercase',
                transition: 'all 0.3s',
                boxShadow: activeTab === tab
                  ? '0 4px 12px rgba(0,0,0,0.15)'
                  : 'none',
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        <div
          style={{
            maxHeight: 'calc(85vh - 220px)',
            overflowY: 'auto',
            paddingRight: 4,
            position: 'relative',
            zIndex: 1,
          }}
        >
          {activeTab === 'point' && (
            <div
              style={{
                background: 'rgba(255,255,255,0.95)',
                borderRadius: 16,
                padding: 24,
              }}
            >
              <h3
                style={{
                  margin: '0 0 20px 0',
                  fontSize: 18,
                  fontWeight: 700,
                  color: '#333',
                }}
              >
                Create Affiliate Link
              </h3>

              <div style={{ marginBottom: 16 }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#555',
                  }}
                >
                  Buyer wallet address
                </label>
                <input
                  type="text"
                  value={walletAddress || ''}
                  readOnly
                  placeholder="Wallet address (required)"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e0e0e0',
                    borderRadius: 12,
                    fontSize: 14,
                    background: '#f5f5f5',
                    color: '#666',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div style={{ marginBottom: 20 }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#555',
                  }}
                >
                  Item link{' '}
                  <a
                    href="https://msu.io/marketplace"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: '#3AA8B8',
                      textDecoration: 'none',
                      fontWeight: 500,
                    }}
                  >
                    (Go to MSU market and copy URL)
                  </a>
                </label>
                <input
                  type="text"
                  value={itemLink}
                  onChange={(e) => setItemLink(e.target.value)}
                  placeholder="Put a MSU market URL to the item you want to purchase."
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e0e0e0',
                    borderRadius: 12,
                    fontSize: 14,
                    boxSizing: 'border-box',
                    outline: 'none',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3AA8B8';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e0e0e0';
                  }}
                />
              </div>

              {/* Affiliate link rules 체크박스 */}
              <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  id="agreedToRules"
                  checked={agreedToRules}
                  onChange={(e) => setAgreedToRules(e.target.checked)}
                  style={{
                    width: 18,
                    height: 18,
                    cursor: 'pointer',
                  }}
                />
                <label htmlFor="agreedToRules" style={{ fontSize: 14, color: '#555', cursor: 'pointer' }}>
                  I agree to the{' '}
                  <span
                    onClick={(e) => {
                      e.preventDefault();
                      setShowRulesModal(true);
                    }}
                    style={{
                      color: '#3AA8B8',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      fontWeight: 600,
                    }}
                  >
                    Affiliate link rules
                  </span>
                </label>
              </div>

              <button
                onClick={handleCreateLink}
                disabled={!agreedToRules}
                style={{
                  width: '100%',
                  padding: '14px',
                  border: 'none',
                  borderRadius: 12,
                  background: agreedToRules
                    ? 'linear-gradient(135deg, #3AA8B8 0%, #2A8898 100%)'
                    : 'linear-gradient(135deg, #CCCCCC 0%, #999999 100%)',
                  color: '#fff',
                  fontSize: 16,
                  fontWeight: 700,
                  cursor: agreedToRules ? 'pointer' : 'not-allowed',
                  boxShadow: '0 4px 12px rgba(58, 168, 184, 0.3)',
                  transition: 'transform 0.2s',
                  opacity: agreedToRules ? 1 : 0.6,
                }}
                onMouseEnter={(e) => {
                  if (agreedToRules) e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                Create Affiliate Link
              </button>

              {affiliateUri && (
                <button
                  onClick={handleOpenAffiliateLink}
                  style={{
                    width: '100%',
                    padding: '14px',
                    border: 'none',
                    borderRadius: 12,
                    background: 'linear-gradient(135deg, #FF8C42 0%, #FF6B35 100%)',
                    color: '#fff',
                    fontSize: 16,
                    fontWeight: 700,
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(255, 140, 66, 0.3)',
                    transition: 'transform 0.2s',
                    marginTop: 12,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  Open Affiliate Link
                </button>
              )}
            </div>
          )}

          {activeTab === 'claim' && (
            <div>
              {/* 월별 랭킹 보상 섹션 (1, 2, 3등만 표시) */}
              {prevMonthMyRank && prevMonthMyRank.rank && prevMonthMyRank.rank <= 3 && (() => {
                const prevMonth = new Date().getUTCMonth(); // 0-11 (이전 달)
                const prevMonthYear = prevMonth === 0
                  ? new Date().getUTCFullYear() - 1
                  : new Date().getUTCFullYear();
                const prevMonthIndex = prevMonth === 0 ? 12 : prevMonth;
                const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                                    'July', 'August', 'September', 'October', 'November', 'December'];
                const zodiacNames = ['Sanho', 'Nari', 'Bari', 'Rose Glodeer', 'Mint Glodeer', 'Iris Glodeer',
                                     'Waddle', 'Puff', 'Toddle', 'Amethyst', 'Cosmo Rabbit', 'Serena'];
                const zodiacImages = ['Sanho.png', 'Nari.png', 'Bari.png', 'Rose%20Glodeer.png',
                                      'Mint%20Glodeer.png', 'Iris%20Glodeer.png', 'Waddle.png', 'Puff.png',
                                      'Toddle.png', 'Amethyst.png', 'Cosmo%20Rabbit.png', 'Serena.png'];

                // 실제 이전 달 랭킹 사용
                const userRank = prevMonthMyRank.rank;
                const rewardQuantity = userRank === 1 ? 60 : userRank === 2 ? 30 : 10;

                return (
                  <div
                    style={{
                      background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                      borderRadius: 16,
                      padding: 20,
                      marginBottom: 16,
                      display: 'flex',
                      gap: 20,
                      alignItems: 'center',
                      boxShadow: '0 4px 12px rgba(255, 165, 0, 0.3)',
                      border: '2px solid #FFD700',
                    }}
                  >
                    {/* 보상 이미지 */}
                    <div
                      style={{
                        width: 80,
                        height: 80,
                        borderRadius: 12,
                        overflow: 'hidden',
                        flexShrink: 0,
                        background: 'rgba(255,255,255,0.9)',
                        position: 'relative',
                      }}
                    >
                      <img
                        src={`https://cqdypzoxossuzspcoqor.supabase.co/storage/v1/object/public/pet-images/${zodiacImages[prevMonthIndex - 1]}`}
                        alt="Ranking Reward"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                      <div
                        style={{
                          position: 'absolute',
                          bottom: 4,
                          right: 4,
                          background: '#FF6B35',
                          color: '#fff',
                          fontSize: 11,
                          fontWeight: 700,
                          padding: '2px 6px',
                          borderRadius: 4,
                        }}
                      >
                        ×{rewardQuantity}
                      </div>
                    </div>

                    {/* 보상 정보 */}
                    <div style={{ flex: 1 }}>
                      <h4
                        style={{
                          margin: '0 0 6px 0',
                          fontSize: 16,
                          fontWeight: 700,
                          color: '#333',
                        }}
                      >
                        {monthNames[prevMonthIndex - 1]} Ranking Reward
                      </h4>
                      <p
                        style={{
                          margin: '0 0 8px 0',
                          fontSize: 13,
                          color: '#555',
                          lineHeight: 1.4,
                        }}
                      >
                        Congratulations! You ranked #{userRank} in {monthNames[prevMonthIndex - 1]} {prevMonthYear}.
                      </p>
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          padding: '4px 10px',
                          borderRadius: 6,
                          fontSize: 11,
                          fontWeight: 600,
                          background: 'rgba(255,255,255,0.8)',
                          color: '#FF6B35',
                        }}
                      >
                        🏆 Rank #{userRank} - {zodiacNames[prevMonthIndex - 1]} ×{rewardQuantity}
                      </div>
                    </div>

                    {/* 클레임 버튼 */}
                    <button
                      onClick={handleClaimRankingReward}
                      disabled={rankingRewardClaimed || claimingRankingReward}
                      style={{
                        padding: '12px 24px',
                        border: 'none',
                        borderRadius: 12,
                        background: rankingRewardClaimed
                          ? '#9E9E9E'
                          : 'linear-gradient(135deg, #FF6B35 0%, #FF4500 100%)',
                        color: '#fff',
                        fontSize: 14,
                        fontWeight: 700,
                        cursor: rankingRewardClaimed || claimingRankingReward ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s',
                        whiteSpace: 'nowrap',
                        boxShadow: rankingRewardClaimed ? 'none' : '0 4px 12px rgba(255, 69, 0, 0.3)',
                        opacity: claimingRankingReward ? 0.7 : 1,
                      }}
                    >
                      {claimingRankingReward ? 'Claiming...' : rankingRewardClaimed ? 'Claimed' : 'Claim'}
                    </button>
                  </div>
                );
              })()}

              {claimLoading ? (
                <div
                  style={{
                    background: 'rgba(255,255,255,0.95)',
                    borderRadius: 16,
                    padding: 40,
                    textAlign: 'center',
                    color: '#666',
                  }}
                >
                  Loading claim items...
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {claimItems.map((item) => {
                    const hasClaimedToday = claimedToday[item.id] || false;
                    const hasDailyLimit = item.daily_limit && item.daily_limit > 0;
                    const hasDustRequirement = dustPoints >= item.required_dust;
                    const hasTradeRequirement = item.requires_daily_trade ? hasTradedToday : true;
                    const canClaim = !hasClaimedToday && hasDustRequirement && hasTradeRequirement;

                    const handleClaim = async () => {
                      if (!canClaim) return;
                      if (claimingItemId) return;

                      try {
                        setClaimingItemId(item.id);
                        const result = await claimReward(item.id, dustPoints);

                        if (result.success) {
                          showAlert(`You received pet #${result.petIndex}`, 'Claim Success!', 'success');
                          refreshDustPoints();
                        }
                      } catch (err) {
                        showAlert(err.message || 'Failed to claim reward', 'Claim Failed', 'error');
                      } finally {
                        setClaimingItemId(null);
                      }
                    };

                    return (
                      <div
                        key={item.id}
                        style={{
                          background: 'rgba(255,255,255,0.95)',
                          borderRadius: 16,
                          padding: 20,
                          display: 'flex',
                          gap: 20,
                          alignItems: 'center',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        }}
                      >
                        {/* 아이템 이미지 */}
                        <div
                          style={{
                            width: 80,
                            height: 80,
                            borderRadius: 12,
                            overflow: 'hidden',
                            flexShrink: 0,
                            background: '#f0f0f0',
                          }}
                        >
                          {item.item_image_url && (
                            <img
                              src={item.item_image_url}
                              alt={item.title}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                              }}
                            />
                          )}
                        </div>

                        {/* 아이템 정보 */}
                        <div style={{ flex: 1 }}>
                          <h4
                            style={{
                              margin: '0 0 8px 0',
                              fontSize: 16,
                              fontWeight: 700,
                              color: '#333',
                            }}
                          >
                            {item.title}
                          </h4>
                          <p
                            style={{
                              margin: '0 0 12px 0',
                              fontSize: 13,
                              color: '#666',
                              lineHeight: 1.4,
                            }}
                          >
                            {item.description}
                          </p>

                          {/* 조건 표시 */}
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {hasDailyLimit && hasClaimedToday && (
                              <div
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 4,
                                  padding: '4px 10px',
                                  borderRadius: 6,
                                  fontSize: 11,
                                  fontWeight: 600,
                                  background: 'rgba(156, 39, 176, 0.1)',
                                  color: '#9C27B0',
                                }}
                              >
                                ✓ Claimed Today
                              </div>
                            )}
                            {item.requires_daily_trade && (
                              <div
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 4,
                                  padding: '4px 10px',
                                  borderRadius: 6,
                                  fontSize: 11,
                                  fontWeight: 600,
                                  background: hasTradedToday
                                    ? 'rgba(76, 175, 80, 0.1)'
                                    : 'rgba(255, 152, 0, 0.1)',
                                  color: hasTradedToday ? '#4CAF50' : '#FF9800',
                                }}
                              >
                                {hasTradedToday ? '✓' : '⏰'} Daily Trade
                              </div>
                            )}
                            {item.required_dust > 0 && (
                              <div
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 4,
                                  padding: '4px 10px',
                                  borderRadius: 6,
                                  fontSize: 11,
                                  fontWeight: 600,
                                  background:
                                    dustPoints >= item.required_dust
                                      ? 'rgba(76, 175, 80, 0.1)'
                                      : 'rgba(244, 67, 54, 0.1)',
                                  color:
                                    dustPoints >= item.required_dust ? '#4CAF50' : '#F44336',
                                }}
                              >
                                <img src={dustIcon} alt="dust" style={{ width: 14, height: 14 }} /> {item.required_dust.toLocaleString()} Dust
                              </div>
                            )}
                          </div>
                        </div>

                        {/* 클레임 버튼 */}
                        <button
                          onClick={handleClaim}
                          disabled={!canClaim || claimingItemId === item.id}
                          style={{
                            padding: '12px 24px',
                            border: 'none',
                            borderRadius: 12,
                            background: canClaim
                              ? 'linear-gradient(135deg, #3AA8B8 0%, #2A8898 100%)'
                              : '#e0e0e0',
                            color: canClaim ? '#fff' : '#999',
                            fontSize: 14,
                            fontWeight: 700,
                            cursor: canClaim ? 'pointer' : 'not-allowed',
                            transition: 'all 0.3s',
                            whiteSpace: 'nowrap',
                            opacity: claimingItemId === item.id ? 0.6 : 1,
                          }}
                        >
                          {claimingItemId === item.id ? 'Claiming...' : 'Claim'}
                        </button>
                      </div>
                    );
                  })}

                  {claimItems.length === 0 && (
                    <div
                      style={{
                        background: 'rgba(255,255,255,0.95)',
                        borderRadius: 16,
                        padding: 40,
                        textAlign: 'center',
                        color: '#666',
                      }}
                    >
                      No claim items available
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'ranking' && (
            <div>
              {/* Cycle Selector */}
              <div style={{ marginBottom: 16 }}>
                <select
                  value={cycles.indexOf(selectedCycle)}
                  onChange={(e) => setSelectedCycle(cycles[parseInt(e.target.value)])}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderRadius: 12,
                    fontSize: 14,
                    fontWeight: 600,
                    background: 'rgba(255,255,255,0.95)',
                    color: '#333',
                    cursor: 'pointer',
                    outline: 'none',
                  }}
                >
                  {cycles.map((cycle, idx) => (
                    <option key={idx} value={idx}>
                      {cycle.label}
                    </option>
                  ))}
                </select>
              </div>

              {rankingLoading ? (
                <div style={{ textAlign: 'center', color: '#fff', padding: '40px 0' }}>
                  Loading rankings...
                </div>
              ) : rankingError ? (
                <div style={{ textAlign: 'center', color: '#fff', padding: '40px 0' }}>
                  Error: {rankingError}
                </div>
              ) : (
                <>
                  {/* My Rank */}
                  {myRank && (
                    <div
                      style={{
                        background: 'rgba(255,255,255,0.95)',
                        borderRadius: 16,
                        padding: '16px',
                        marginBottom: 16,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      }}
                    >
                      <div
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #3AA8B8 0%, #2A8898 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 24,
                          flexShrink: 0,
                          overflow: 'hidden',
                        }}
                      >
                        {myRank.profile ? (
                          <img
                            src={myRank.profile}
                            alt={myRank.nickname || 'My profile'}
                            style={{
                              width: '150%',
                              height: '150%',
                              objectFit: 'contain',
                              objectPosition: 'center center',
                              transform: 'translateY(-4px)',
                            }}
                          />
                        ) : (
                          '👤'
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 11,
                            color: '#666',
                            marginBottom: 2,
                            textTransform: 'uppercase',
                            fontWeight: 600,
                          }}
                        >
                          MY RANK
                        </div>
                        <div
                          style={{
                            fontSize: 16,
                            fontWeight: 700,
                            color: '#333',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {myRank.nickname || 'You'}
                        </div>
                      </div>
                      <div
                        style={{
                          background: '#3AA8B8',
                          borderRadius: 12,
                          padding: '8px 16px',
                          color: '#fff',
                          fontWeight: 700,
                          fontSize: 16,
                          textAlign: 'center',
                          minWidth: 80,
                        }}
                      >
                        <div style={{ fontSize: 11, marginBottom: 2 }}>#{myRank.rank}</div>
                        <div>{myRank.total_fee?.toLocaleString() || '0'}</div>
                      </div>
                    </div>
                  )}

                  {/* Top 10 Rankings */}
                  <div
                    style={{
                      maxHeight: myRank ? '400px' : '500px',
                      overflowY: 'auto',
                      paddingRight: 4,
                    }}
                  >
                    {rankings.length === 0 ? (
                      <div
                        style={{
                          background: 'rgba(255,255,255,0.95)',
                          borderRadius: 16,
                          padding: 24,
                          textAlign: 'center',
                          color: '#666',
                        }}
                      >
                        No rankings available for this month
                      </div>
                    ) : (
                      rankings.map((user, idx) => {
                        const rank = idx + 1;
                        const isTop3 = rank <= 3;
                        const isMyRank =
                          walletAddress &&
                          user.wallet_address?.toLowerCase() === walletAddress.toLowerCase();

                        const getRankIcon = (r) => {
                          if (r === 1) return '🥇';
                          if (r === 2) return '🥈';
                          if (r === 3) return '🥉';
                          return `${r}.`;
                        };

                        return (
                          <div
                            key={user.wallet_address || idx}
                            style={{
                              background: isMyRank
                                ? 'rgba(255,255,255,0.95)'
                                : 'rgba(255,255,255,0.9)',
                              borderRadius: 12,
                              padding: '14px 16px',
                              marginBottom: 10,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 12,
                              boxShadow: isMyRank
                                ? '0 4px 12px rgba(0,0,0,0.2)'
                                : '0 2px 8px rgba(0,0,0,0.1)',
                              border: isMyRank ? '2px solid #3AA8B8' : 'none',
                            }}
                          >
                            {/* Rank */}
                            <div
                              style={{
                                width: 36,
                                height: 36,
                                borderRadius: '50%',
                                background: isTop3
                                  ? 'linear-gradient(135deg, #3AA8B8 0%, #2A8898 100%)'
                                  : '#e0e0e0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: isTop3 ? 20 : 14,
                                fontWeight: 700,
                                color: isTop3 ? '#fff' : '#666',
                                flexShrink: 0,
                              }}
                            >
                              {getRankIcon(rank)}
                            </div>

                            {/* Avatar */}
                            <div
                              style={{
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #3AA8B8 0%, #2A8898 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 20,
                                flexShrink: 0,
                                overflow: 'hidden',
                              }}
                            >
                              {user.profile ? (
                                <img
                                  src={user.profile}
                                  alt={user.nickname || 'profile'}
                                  style={{
                                    width: '150%',
                                    height: '150%',
                                    objectFit: 'contain',
                                    objectPosition: 'center center',
                                    transform: 'translateY(-4px)',
                                  }}
                                />
                              ) : (
                                '👤'
                              )}
                            </div>

                            {/* Nickname */}
                            <div
                              style={{
                                flex: 1,
                                minWidth: 0,
                                fontSize: 15,
                                fontWeight: 600,
                                color: '#333',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {user.nickname || `Player ${idx + 1}`}
                            </div>

                            {/* Dust Points */}
                            <div
                              style={{
                                background: '#3AA8B8',
                                borderRadius: 8,
                                padding: '6px 14px',
                                color: '#fff',
                                fontWeight: 700,
                                fontSize: 15,
                                minWidth: 70,
                                textAlign: 'center',
                              }}
                            >
                              {user.total_fee?.toLocaleString() || '0'}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div
              style={{
                background: 'rgba(255,255,255,0.95)',
                borderRadius: 16,
                padding: 24,
              }}
            >
              <h3
                style={{
                  margin: '0 0 16px 0',
                  fontSize: 18,
                  fontWeight: 700,
                  color: '#333',
                }}
              >
                Affiliate Trade History
              </h3>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#666' }}>
                  Loading...
                </div>
              ) : !walletAddress ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#666' }}>
                  Please connect your wallet to view history.
                </div>
              ) : histories.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#666' }}>
                  No trade history yet.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {histories.map((history) => (
                    <div
                      key={history.id}
                      style={{
                        background: '#fff',
                        border: '1px solid #e0e0e0',
                        borderRadius: 12,
                        padding: 16,
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: 12,
                        }}
                      >
                        <div>
                          <div
                            style={{
                              fontSize: 14,
                              fontWeight: 700,
                              color: '#3AA8B8',
                              marginBottom: 4,
                            }}
                          >
                            <img src={dustIcon} alt="dust" style={{ width: 16, height: 16, marginRight: 4, verticalAlign: 'middle' }} />{history.fee ? Number(history.fee).toLocaleString() : '0'} Dusts
                          </div>
                          <div
                            style={{
                              fontSize: 11,
                              color: '#999',
                            }}
                          >
                            {new Date(history.purchase_date + 'Z').toLocaleString()}
                          </div>
                        </div>
                        <div
                          style={{
                            background:
                              history.status === 'completed'
                                ? 'rgba(76, 175, 80, 0.1)'
                                : 'rgba(255, 152, 0, 0.1)',
                            color:
                              history.status === 'completed' ? '#4CAF50' : '#FF9800',
                            padding: '4px 10px',
                            borderRadius: 6,
                            fontSize: 11,
                            fontWeight: 600,
                            textTransform: 'uppercase',
                          }}
                        >
                          {history.status}
                        </div>
                      </div>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr 1fr',
                          gap: 12,
                          marginBottom: 12,
                        }}
                      >
                        <div>
                          <div
                            style={{
                              fontSize: 10,
                              color: '#999',
                              marginBottom: 4,
                              textTransform: 'uppercase',
                            }}
                          >
                            Price
                          </div>
                          <div
                            style={{
                              fontSize: 14,
                              fontWeight: 600,
                              color: '#333',
                            }}
                          >
                            {history.price ? Number(history.price).toLocaleString() : '0'}{' '}
                            NESO
                          </div>
                        </div>
                        <div>
                          <div
                            style={{
                              fontSize: 10,
                              color: '#999',
                              marginBottom: 4,
                              textTransform: 'uppercase',
                            }}
                          >
                            Fee
                          </div>
                          <div
                            style={{
                              fontSize: 14,
                              fontWeight: 600,
                              color: '#666',
                            }}
                          >
                            {history.fee ? Number(history.fee).toLocaleString() : '0'}{' '}
                            NESO
                          </div>
                        </div>
                        <div>
                          <div
                            style={{
                              fontSize: 10,
                              color: '#999',
                              marginBottom: 4,
                              textTransform: 'uppercase',
                            }}
                          >
                            Quantity
                          </div>
                          <div
                            style={{
                              fontSize: 14,
                              fontWeight: 600,
                              color: '#333',
                            }}
                          >
                            {history.quantity || 0}
                          </div>
                        </div>
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: 10,
                            color: '#999',
                            marginBottom: 4,
                            textTransform: 'uppercase',
                          }}
                        >
                          Transaction Hash
                        </div>
                        <a
                          href={`https://baobab.klaytnscope.com/tx/${history.transaction_hash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            fontSize: 11,
                            color: '#3AA8B8',
                            textDecoration: 'none',
                            fontFamily: 'monospace',
                            wordBreak: 'break-all',
                          }}
                        >
                          {history.transaction_hash || 'N/A'}
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Affiliate Link Rules Modal */}
      {showRulesModal && (
        <div
          onClick={() => setShowRulesModal(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10001,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 20,
              padding: '32px',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '80vh',
              overflowY: 'auto',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              position: 'relative',
            }}
          >
            <button
              onClick={() => setShowRulesModal(false)}
              style={{
                position: 'absolute',
                top: 16,
                right: 16,
                background: 'rgba(0,0,0,0.1)',
                border: 'none',
                fontSize: 24,
                borderRadius: '50%',
                width: 36,
                height: 36,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              ✕
            </button>

            <h2
              style={{
                margin: '0 0 24px 0',
                fontSize: 24,
                fontWeight: 700,
                color: '#333',
              }}
            >
              Affiliate Link Rules
            </h2>

            <div style={{ fontSize: 15, lineHeight: 1.8, color: '#444' }}>
              <p style={{ marginBottom: 16, fontWeight: 600, color: '#3AA8B8' }}>
                Keep these in mind to make sure every purchase earns you points:
              </p>

              <ul style={{ paddingLeft: 24, margin: 0 }}>
                <li style={{ marginBottom: 16 }}>
                  <strong>Only NFT items and characters are eligible for rewards</strong> — FT purchases are not rewarded.
                </li>
                <li style={{ marginBottom: 16 }}>
                  <strong>The purchase must match the affiliate link you clicked</strong> — same item name or exact token ID. For anything else, click that item's link first.
                </li>
                <li style={{ marginBottom: 16 }}>
                  <strong>Each affiliate click covers one purchase</strong> — grab a fresh link for repeat buys.
                </li>
                <li style={{ marginBottom: 16 }}>
                  <strong>Use the same wallet here and on MSU</strong> so your rewards land safely.
                </li>
                <li style={{ marginBottom: 16 }}>
                  <strong>Links stay active for one hour</strong>; shop soon after you click through.
                </li>
              </ul>
            </div>

            <button
              onClick={() => setShowRulesModal(false)}
              style={{
                marginTop: 24,
                width: '100%',
                padding: '12px',
                border: 'none',
                borderRadius: 12,
                background: 'linear-gradient(135deg, #3AA8B8 0%, #2A8898 100%)',
                color: '#fff',
                fontSize: 16,
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(58, 168, 184, 0.3)',
              }}
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* Alert Modal */}
      {alertModal.show && (
        <div
          onClick={() => setAlertModal({ ...alertModal, show: false })}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10002,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 20,
              padding: 32,
              maxWidth: 400,
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
              position: 'relative',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}
          >
            {/* Icon */}
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                background: alertModal.type === 'success'
                  ? 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)'
                  : alertModal.type === 'error'
                    ? 'linear-gradient(135deg, #f44336 0%, #c62828 100%)'
                    : alertModal.type === 'warning'
                      ? 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)'
                      : 'linear-gradient(135deg, #3AA8B8 0%, #2A8898 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                fontSize: 28,
              }}
            >
              {alertModal.type === 'success' ? '✓' :
                alertModal.type === 'error' ? '✕' :
                  alertModal.type === 'warning' ? '!' : 'i'}
            </div>

            {/* Title */}
            <h2
              style={{
                margin: '0 0 16px 0',
                fontSize: 20,
                fontWeight: 700,
                color: '#333',
                textAlign: 'center',
              }}
            >
              {alertModal.title}
            </h2>

            {/* Message */}
            <div
              style={{
                fontSize: 15,
                lineHeight: 1.6,
                color: '#555',
                textAlign: 'center',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                marginBottom: 24,
                maxWidth: '100%',
                overflow: 'hidden',
              }}
            >
              {alertModal.message}
            </div>

            {/* Button */}
            <button
              onClick={() => setAlertModal({ ...alertModal, show: false })}
              style={{
                width: '100%',
                padding: '14px',
                border: 'none',
                borderRadius: 12,
                background: alertModal.type === 'success'
                  ? 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)'
                  : alertModal.type === 'error'
                    ? 'linear-gradient(135deg, #f44336 0%, #c62828 100%)'
                    : alertModal.type === 'warning'
                      ? 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)'
                      : 'linear-gradient(135deg, #3AA8B8 0%, #2A8898 100%)',
                color: '#fff',
                fontSize: 16,
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
